import { useState, useCallback } from 'react';
import { AppSidebar } from '@/components/AppSidebar';
import { DashboardOverview } from '@/pages/DashboardOverview';
import { Dashboard } from '@/pages/Dashboard';
import { Resources } from '@/pages/Resources';
import { Projects } from '@/pages/Projects';
import { Settings } from '@/pages/Settings';
import { UnallocatedBookings } from '@/pages/UnallocatedBookings';
import { UtilisationReport } from '@/pages/reports/UtilisationReport';
import { UtilisationForecast } from '@/pages/reports/UtilisationForecast';
import { AvailableCapacity } from '@/pages/reports/AvailableCapacity';
import { ResourceAllocation } from '@/pages/reports/ResourceAllocation';
import { ProjectStaffing } from '@/pages/reports/ProjectStaffing';
import { BenchReport } from '@/pages/reports/BenchReport';
import { SchedulerProvider } from '@/contexts/SchedulerContext';
import { FilterProvider } from '@/contexts/FilterContext';
import { UserProvider } from '@/contexts/UserContext';
import { SavedBookingTypesProvider } from '@/contexts/SavedBookingTypesContext';
import { FieldOrderingProvider } from '@/contexts/FieldOrderingContext';
import { ClipboardProvider } from '@/contexts/ClipboardContext';
import { DataModeProvider } from '@/contexts/DataModeContext';
import { CompanyProvider } from '@/contexts/CompanyContext';
import { ProductTour } from '@/components/ProductTour';
import { WelcomeModal } from '@/components/WelcomeModal';
import { TourFeedbackModal } from '@/components/TourFeedbackModal';
import { useTour } from '@/contexts/TourContext';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import { WhatIfScenario } from '@/types';

export default function App() {
  const [currentPage, setCurrentPage] = useState<'overview' | 'calendar' | 'heatmap' | 'resources' | 'projects' | 'settings' | 'unallocated' | 'dependent-fields' | 'report-utilisation' | 'report-utilisation-forecast' | 'report-available-capacity' | 'report-resource-allocation' | 'report-project-staffing' | 'report-bench'>('calendar');
  const [settingsTab, setSettingsTab] = useState<string>('settings');
  const { showFeedbackModal, setShowFeedbackModal } = useTour();

  // What-if scenario: set by the heatmap suggestions panel, consumed by the calendar view
  const [pendingWhatIf, setPendingWhatIf] = useState<WhatIfScenario | null>(null);

  /** Called by BurnoutRiskPanelWrapper when Apply/Assign is pressed */
  const handleNavigateToCalendarWithWhatIf = useCallback((scenario: WhatIfScenario) => {
    setPendingWhatIf(scenario);
    setCurrentPage('calendar');
  }, []);

  /** Called by ResourceCalendar when the scenario is committed or discarded */
  const handleWhatIfComplete = useCallback((navigateBackToHeatmap?: boolean) => {
    setPendingWhatIf(null);
    if (navigateBackToHeatmap) {
      setCurrentPage('heatmap');
    }
  }, []);

  return (
    <UserProvider>
      <DataModeProvider>
        <CompanyProvider>
        <SchedulerProvider>
          <SavedBookingTypesProvider>
            <FieldOrderingProvider>
              <FilterProvider>
                <ClipboardProvider>
                  <SidebarProvider
                    defaultOpen={false}
                    style={{ "--sidebar-width": "13rem" } as React.CSSProperties}
                    className="fixed inset-0 overflow-hidden"
                  >
                    <AppSidebar
                      currentPage={currentPage}
                      onNavigate={(page) => setCurrentPage(page as typeof currentPage)}
                      settingsTab={settingsTab}
                      onNavigateSettings={(tab) => { setSettingsTab(tab); setCurrentPage('settings'); }}
                    />

                    <SidebarInset className="h-screen overflow-hidden">
                      {currentPage === 'overview' && <DashboardOverview />}
                      {currentPage === 'calendar' && (
                        <Dashboard
                          whatIfScenario={pendingWhatIf}
                          onWhatIfComplete={handleWhatIfComplete}
                        />
                      )}
                      {currentPage === 'heatmap' && (
                        <Dashboard
                          heatmapMode
                          onNavigateToCalendarWithWhatIf={handleNavigateToCalendarWithWhatIf}
                        />
                      )}
                      {currentPage === 'resources' && <Resources />}
                      {currentPage === 'projects' && <Projects />}
                      {currentPage === 'settings' && <Settings initialTab={settingsTab} />}
                      {currentPage === 'unallocated' && (
                        <UnallocatedBookings
                          onNavigateToPlanner={() => setCurrentPage('calendar')}
                        />
                      )}
                      {currentPage === 'report-utilisation' && <UtilisationReport />}
                      {currentPage === 'report-utilisation-forecast' && <UtilisationForecast />}
                      {currentPage === 'report-available-capacity' && <AvailableCapacity />}
                      {currentPage === 'report-resource-allocation' && <ResourceAllocation />}
                      {currentPage === 'report-project-staffing' && <ProjectStaffing />}
                      {currentPage === 'report-bench' && <BenchReport />}
                    </SidebarInset>
                  </SidebarProvider>

                  <WelcomeModal />
                  <ProductTour />
                  <TourFeedbackModal
                    isOpen={showFeedbackModal}
                    onClose={() => setShowFeedbackModal(false)}
                  />
                </ClipboardProvider>
              </FilterProvider>
            </FieldOrderingProvider>
          </SavedBookingTypesProvider>
        </SchedulerProvider>
        </CompanyProvider>
      </DataModeProvider>
    </UserProvider>
  );
}
